/**
 * useDictCard — ленивые данные и мутации карточки позиции Справочников
 * (screen-21, DICT-2 каркас для карточек DICT-3): GET /:id/card по открытию
 * аккордеона, PUT полей, PUT /:id/bindings (replace-set, у расходника все
 * четыре массива обязательны), POST /:id/purchases, медиа покупки —
 * FormData по файлу за запрос (POST /api/dict-media/:purchaseId, Д8).
 * Optimistic-слоёв нет: карточка правится в локальном editSt (эталон) и
 * коммитится «Сохранить»; после успеха хост зовёт reload() + sa.refetch()
 * (или sa.patchRow/flashNow). Ошибки мутаций резолвятся текстом причины
 * (dictErr: 05.10 — отказ по правам больше не выдаётся за «ошибку сети»);
 * hint показывает хост.
 */
import { useEffect, useState } from "react";
import { api } from "../api/client";
import { ApiException, type ApiOk } from "../api/types";
import { dictErr } from "../screens/eo/dictErr";
import type {
  ConsBindingsBody, ConsCardWire, ResBindingsBody, ResCardWire,
} from "../api/dictTypes";
import type { DictKind } from "./useDictsAdmin";

/** 10.09 (владелец): округление ушло из общих полей в каждую привязку — здесь его больше нет. */
export interface ConsFieldsPatch { name?: string; unit?: string; cost?: number }
export interface ResFieldsPatch { name?: string; cost?: number; branch_id?: string | null; round_to?: number }
/** Причина провала загрузки фото: big — 413 (>20 МБ, повтор бессмыслен),
 *  type — 415 (не изображение), denied — 403/404 (нет прав, повтор бессмыслен),
 *  net — сеть/сбой сервера (можно повторить). */
export type DictUploadFail = "big" | "type" | "denied" | "net";

export interface DictCardApi {
  /** Заполнен ровно один — по kind хука. */
  consCard: ConsCardWire | null;
  resCard: ResCardWire | null;
  loading: boolean;
  err: boolean;
  reload: () => void;
  /** null — сохранено; строка — причина отказа (dictErr). */
  saveFields: (patch: ConsFieldsPatch | ResFieldsPatch) => Promise<string | null>;
  saveBindings: (body: ConsBindingsBody | ResBindingsBody) => Promise<string | null>;
  /** qty — только у расходника (у ресурса сервер пишет 0). Вернёт id покупки
   *  и purchased_at сервера (реальная дата строки без рефетча карточки, Д1). */
  addPurchase: (p: { qty?: number; sum: number; purchased_at?: string; note?: string }) =>
    Promise<{ id: number; purchased_at: string } | { error: string }>;
  /** По файлу за запрос; url — подписанный stream для немедленного превью.
   *  Провал — {fail}: хост различает «файл велик/не картинка» от сети (ретрай). */
  uploadMedia: (purchaseId: number, file: File) =>
    Promise<{ media_id: number; url: string } | { fail: DictUploadFail }>;
}

export function useDictCard(kind: DictKind, id: string): DictCardApi {
  const [consCard, setConsCard] = useState<ConsCardWire | null>(null);
  const [resCard, setResCard] = useState<ResCardWire | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [tick, setTick] = useState(0);
  const base = `/api/${kind === "c" ? "consumables" : "resources"}/${encodeURIComponent(id)}`;

  useEffect(() => {
    let dead = false;
    setLoading(true);
    setErr(false);
    const load = kind === "c"
      ? api<ConsCardWire>(`${base}/card`).then((w) => { if (!dead) { setConsCard(w); setResCard(null); } })
      : api<ResCardWire>(`${base}/card`).then((w) => { if (!dead) { setResCard(w); setConsCard(null); } });
    void load
      .catch(() => { if (!dead) setErr(true); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, id, tick]);

  const saveFields = (patch: ConsFieldsPatch | ResFieldsPatch): Promise<string | null> =>
    api<ApiOk>(base, { method: "PUT", body: patch })
      .then(() => null)
      .catch(dictErr);

  const saveBindings = (body: ConsBindingsBody | ResBindingsBody): Promise<string | null> =>
    api<ApiOk>(`${base}/bindings`, { method: "PUT", body })
      .then(() => null)
      .catch(dictErr);

  const addPurchase = (
    p: { qty?: number; sum: number; purchased_at?: string; note?: string },
  ): Promise<{ id: number; purchased_at: string } | { error: string }> =>
    /* retries:0 (паритет с uploadMedia): авто-повтор POST при потерянном
       ответе задваивал бы строку purchases (сервер неидемпотентен). */
    api<ApiOk & { purchased_at?: string }>(`${base}/purchases`, { method: "POST", body: p, retries: 0 })
      .then((r) => (typeof r.id === "number"
        ? { id: r.id, purchased_at: typeof r.purchased_at === "string" ? r.purchased_at : "" }
        : { error: "сервер не вернул номер покупки — обновите карточку" }))
      .catch((e: unknown) => ({ error: dictErr(e) }));

  const uploadMedia = (
    purchaseId: number, file: File,
  ): Promise<{ media_id: number; url: string } | { fail: DictUploadFail }> => {
    const fd = new FormData();
    fd.append("file", file);
    return api<{ ok: boolean; media_id: number; url: string }>(
      `/api/dict-media/${purchaseId}`, { method: "POST", body: fd, retries: 0 },
    )
      .then((r) => ({ media_id: r.media_id, url: r.url }))
      .catch((err: unknown) => ({
        fail: err instanceof ApiException && err.status === 413 ? "big" as const
          : err instanceof ApiException && err.status === 415 ? "type" as const
          : err instanceof ApiException && (err.status === 403 || err.status === 404) ? "denied" as const : "net" as const,
      }));
  };

  return {
    consCard, resCard, loading, err,
    reload: () => setTick((n) => n + 1),
    saveFields, saveBindings, addPurchase, uploadMedia,
  };
}
